import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import {
	usePublish,
	type ValidSourceIdsForChatCallToAction,
	type Payload,
} from '@atlaskit/rovo-triggers';

import { type LoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariantState';
import { fg } from '@confluence/feature-gating';

import { rovoLoomScriptMessages } from './messages';

const CROSS_JOIN_SOURCE_ID = 'confluence-page-loom-script-cross-join-touchpoint' as const;
const CO_USE_SOURCE_ID = 'confluence-page-loom-script-co-use-touchpoint' as const;

interface UseRovoLoomScriptEntryPointResult {
	/**
	 * function which will open the rovo chat, will be `undefined`
	 * if the criteria for opening the script is not met
	 */
	openRovoLoomScript?: () => void;
}

/**
 * @param entryPointVariant VERY important `CO_USE` or `CROSS_JOIN` will determine
 * the type of CTA that shows in the rovo chat
 * @returns `openRovoLoomScript` the function that will handle opening ROVO chat in the
 * sidebar with the prompt for a loom script, or `undefined` if we can't open it for the variation.
 */
export const useRovoLoomScriptEntryPoint = (
	entryPointVariant: LoomEntryPointVariant['entryPointVariant'],
	contentId?: string,
): UseRovoLoomScriptEntryPointResult => {
	const publish = usePublish('ai-mate');

	const { formatMessage } = useIntl();

	const openRovoLoomScript = useCallback(() => {
		let sourceId: ValidSourceIdsForChatCallToAction;

		switch (entryPointVariant) {
			case 'CO_USE':
				sourceId = CO_USE_SOURCE_ID;
				break;
			case 'CROSS_JOIN':
				sourceId = CROSS_JOIN_SOURCE_ID;
				break;
			default:
				/**
				 * We should NOT allow consumers to open the script for any other variations.
				 */
				return;
		}

		const payload: Payload = {
			type: 'chat-new',
			data: {
				name: formatMessage(rovoLoomScriptMessages.rovoLoomScriptChatTitle),
				dialogues: [],
				prompt: formatMessage(rovoLoomScriptMessages.rovoLoomScriptPrompt),
				sourceId:
					contentId && fg('rovo-chat-glitchiness-fix-loom-script-confluence')
						? `${sourceId}-${contentId}`
						: sourceId,
			},
			source: 'confluence',
			interactionSource: sourceId,
			openChat: true,
		};

		publish(payload);
	}, [publish, entryPointVariant, formatMessage, contentId]);

	return {
		openRovoLoomScript,
	};
};
